import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import ResumeItem from "@/common/components/ResumeItem";
import SelectController from "@/common/components/SelectController";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import { cn } from "@/common/utils/cn";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon, SearchIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { dashboardKind } from "../../constants/dashboards/dashboardKind.constant";
import { useCreateDashboardComponent } from "../../hooks/dashboards/useCreateDashboardComponent";
import { useDashboardComponentsGenerators } from "../../hooks/dashboards/useDashboardComponentsGenerators";
import { createDashboardComponentSchema, CreateDashboardComponentValidationSchema } from "../../schemas/dashboards/dashboardComponent";
import { DashboardFilters } from "../../types/dashboards/dashboard.type";
import { DashboardComponentFiltersProp, DashboardComponentGeneratorData, DashboardComponentKind } from "../../types/dashboards/dashboardComponent.type";
import { getDashboardFilterLabel } from "../../utils/dashboards/getDashboardFilterLabel";
import DashboardComponentPreview from "./DashboardComponentPreview";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";

interface CreateDashboardComponentProps {
  dashboardId: string;
  initialPosition: {
    x: number;
    y: number;
  };
  onCreateComponent?: () => void;
  width?: number;
  filters?: DashboardComponentFiltersProp;
  dashboardDesiredFilters?: DashboardFilters[];
}

const CreateDashboardComponent = ({
  dashboardId,
  initialPosition,
  onCreateComponent,
  width,
  filters,
  dashboardDesiredFilters,
}: CreateDashboardComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { setMessages } = useModalMessagesSimple({
    isError: true,
  });

  const [search, setSearch] = useDebounce('');

  const [selectedGenerator, setSelectedGenerator] = useState<DashboardComponentGeneratorData>();

  const queryClient = useQueryClient();

  const {
    control,
    setValue,
    setError,
    handleSubmit,
    reset,
  } = useForm<CreateDashboardComponentValidationSchema>({
    resolver: zodResolver(createDashboardComponentSchema),
  });

  const watchGeneratorKlass = useWatch({
    control,
    name: "generator_klass",
  });

  const watchKind = useWatch({
    control,
    name: 'kind',
  });

  const {
    data: generators,
    dataUpdatedAt: generatorsUpdatedAt,
  } = useDashboardComponentsGenerators<DashboardComponentGeneratorData[]>({
    filters: {
      search,
    },
  });

  const kindOptions = useMemo(() => {
    if (!watchGeneratorKlass || !selectedGenerator) {
      return [];
    }

    return selectedGenerator.available_kinds?.map((kind) => ({
      value: kind,
      label: dashboardKind[kind] || kind,
    }));
  }, [watchGeneratorKlass]);

  const {
    mutate: createComponent,
    isPending: createComponentIsPending,
  } = useCreateDashboardComponent({
    dashboardId: dashboardId,
    initialPosition,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["dashboard", dashboardId],
        });

        setIsOpen(false);

        onCreateComponent?.();
      },
      onError: FormTransformers.errorsTransformer({
        setError,
        setMessages,
      }),
    },
  });

  const onSubmit: SubmitHandler<CreateDashboardComponentValidationSchema> = (
    data,
  ) => createComponent(data);

  const filteredGenerators = useMemo(() => {
    if (!search) {
      return generators;
    }

    const filteredDashboards = generators.filter((generator) => {
      return generator.title.toUpperCase().includes(search.toUpperCase());
    });

    return filteredDashboards;
  }, [generatorsUpdatedAt, search]);

  useEffect(() => {
    reset();
    setSelectedGenerator(undefined);
    setSearch('');
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={
        <Button className="text-nowrap h-10">
          {width && width < 700 ? (
            <PlusIcon size={16} />
          ) : 'Adicionar gráfico'}
        </Button>
      }
      title="Novo gráfico"
      className="rounded-lg overflow-hidden w-[90vw] max-w-[920px] h-[90vh] max-h-[760px] bg-black-900 flex flex-col"
      bodyClassName="text-white-800 flex-1 flex flex-col overflow-hidden"
      showClose={false}
      useCancellableFooter
      cancelButtonClassName="bg-white-100"
      confirmButtonClassName="bg-primary-blue"
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonIsLoading={createComponentIsPending}
      body={
        <form className="h-full flex-1 overflow-hidden flex gap-3">
          <div className="flex-1 flex flex-col overflow-hidden gap-3">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e: any) => setSearch(e.target.value)}
            />

            <div className="flex flex-col gap-2 overflow-auto thin-scroll pr-1">
              {filteredGenerators.length > 0 ? filteredGenerators.map((generator) => (
                <div
                  className={cn(
                    'bg-white-100 hover:bg-white-200 transition-colors cursor-pointer rounded-md p-3 flex flex-col gap-1',
                    selectedGenerator?.klass === generator.klass && 'bg-white-200 border border-white-300'
                  )}
                  onClick={() => {
                    setSelectedGenerator(generator);

                    setValue('generator_klass', {
                      value: generator.klass,
                      label: generator.title,
                    });

                    setValue('kind', {
                      value: generator.available_kinds[0],
                      label: dashboardKind[generator.available_kinds[0]],
                    });
                  }}
                >
                  <p className="text-sm">
                    {generator.title}
                  </p>

                  {generator.subtitle && (
                    <p className="text-xs text-white-300">
                      {generator.subtitle}
                    </p>
                  )}
                </div>
              )) : (
                <EmptyDataMessage />
              )}
            </div>
          </div>

          <div className="h-full w-px bg-neutral-op-8" />

          <div className="flex-1 flex flex-col gap-3 overflow-hidden">
            {selectedGenerator && (
              <>
                <DashboardComponentPreview
                  generator={selectedGenerator}
                  kind={watchKind.value as DashboardComponentKind}
                  filters={filters}
                  dashboardDesiredFilters={dashboardDesiredFilters}
                />

                <SelectController
                  control={control}
                  name="kind"
                  label="Tipo do gráfico"
                  markAsRequired
                  options={kindOptions}
                />

                <div className="flex flex-col gap-3 overflow-auto thin-scroll">
                  <ResumeItem
                    className="text-sm"
                    label="Título"
                    value={selectedGenerator.title}
                  />

                  {selectedGenerator.subtitle && (
                    <ResumeItem
                      className="text-sm"
                      label="Descrição"
                      value={selectedGenerator.subtitle}
                    />
                  )}

                  {Object.keys(selectedGenerator.filters).length > 0 && (
                    <ResumeItem
                      className="text-sm"
                      label="Filtros disponíveis"
                      value={
                        '• ' + Object.keys(selectedGenerator.filters)
                          .map(getDashboardFilterLabel)
                          .filter(Boolean)
                          .join('\n• ')
                      }
                      valueClassName="whitespace-pre-wrap"
                    />
                  )}
                </div>
              </>
            )}

            {!selectedGenerator && (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-white-400 italic">
                  Selecione o gráfico na lista à esquerda
                </p>
              </div>
            )}
          </div>
        </form>
      }
    />
  );
};

export default CreateDashboardComponent;
